/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable react/display-name */
import { Box, Divider, List, Typography, useTheme } from "@mui/material";
import { usePopup } from "@nodeon/react-components";
import { addMinutes, format } from "date-fns";
import React, { useMemo } from "react";
import { BerthOrderListDto, useBerthApi } from "~/api/GeneratedApi";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { useDateFormatter } from "~/utils/hooks/useDateFormatter";
import { BerthOrderDialog } from "./BerthOrderDialog";
import { SHIP_COLORS } from "./BerthOrdersList";
import { TimeBlockListItem } from "./TimeblockListItem";

interface Props {
  date: Date;
  berthId: number;
  orders?: BerthOrderListDto[];
  firstColumn?: boolean;
  refetch?: () => void;
  colors?: Record<string, string>;
  solo?: boolean;
}

export interface Timeblock {
  date: Date;
  order?: BerthOrderListDto;
  orderIndex?: number;
  first?: boolean;
  last?: boolean;
  color?: string;
  nextBlock?: Timeblock;
}

const findTimeblockOrder = (
  orders: BerthOrderListDto[],
  time: Date,
): BerthOrderListDto | undefined =>
  orders.find(
    (o) =>
      time.valueOf() >= o.startTimestamp.valueOf() &&
      time.valueOf() < o.endTimestamp.valueOf(),
  );

export const BerthOrdersDayList = React.memo(
  ({
    date,
    orders: parentOrders,
    firstColumn,
    berthId,
    refetch: parentRefetch,
    colors: parentColors,
    solo,
  }: Props): JSX.Element => {
    const dateFormatter = useDateFormatter();
    const theme = useTheme();
    const { show } = usePopup();
    const berthApi = useBerthApi();

    const { data: berthOrders, refetch: berthRefetch } = useCustomQuery(
      { key: "berth-orders", id: berthId },
      () => berthApi.getBerthOrders(berthId),
      {
        select: (o) => o.results,
        enabled: !parentOrders,
      },
    );

    const orders = useMemo(
      () => parentOrders ?? berthOrders ?? [],
      [berthOrders, parentOrders],
    );

    const refetch = useMemo(
      () => parentRefetch ?? berthRefetch,
      [berthRefetch, parentRefetch],
    );

    const colors = useMemo(() => {
      if (parentColors) return parentColors;
      const uniqueShips = new Set(
        Array.from(orders).map((x) => x.vessel!.mmsi),
      );

      return Array.from(uniqueShips)
        .sort((a, b) => a - b)
        .reduce(
          (tot, curr, index) => ({ ...tot, [curr]: SHIP_COLORS[index] }),
          {},
        );
    }, [orders, parentColors]);

    const timeBlocks = React.useMemo(() => {
      const blocks: Timeblock[] = [];
      for (let i = 0; i < 48; i++) {
        const d = addMinutes(date, 30 * i);
        const order = findTimeblockOrder(orders, d);
        const first = order && !blocks.some((x) => x.order?.id === order.id);
        const last =
          order &&
          !findTimeblockOrder(
            orders.filter((x) => x.id === order.id),
            addMinutes(d, 30),
          );
        blocks.push({
          date: d,
          order,
          orderIndex: order ? orders.indexOf(order) : undefined,
          first,
          last,
          color: order ? colors[order.vessel!.mmsi] : undefined,
        });
      }
      for (let i = 0; i < 47; i++) {
        const block = blocks[i];
        block.nextBlock = blocks[i + 1];
      }
      return blocks;
    }, [colors, date, orders]);

    const renderedBlocks = useMemo(
      () =>
        timeBlocks.map((block, i) => (
          <TimeBlockListItem
            block={block}
            firstColumn={firstColumn ?? false}
            time={firstColumn ? format(block.date, "HH:mm") : ""}
            last={i === timeBlocks.length - 1}
            key={block.date.valueOf()}
            disableGutters
            style={{
              minHeight: 36,
              maxHeight: 36,
            }}
            onClick={() => {
              show(
                ({ close }) => (
                  <BerthOrderDialog
                    open
                    berthId={berthId}
                    activeAction={block.order ? "edit" : "add"}
                    onClose={close}
                    berthOrderId={block.order?.id ?? undefined}
                    startTimestamp={block.date}
                    onSubmit={() => refetch?.()}
                  />
                ),
                { noDialog: true },
              );
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: (th) =>
                  th.palette.getContrastText(block.color ?? "#eaeff1"),
              }}
              title={block.order?.vessel?.name}
              noWrap
            >
              {block.order?.vessel?.name}
            </Typography>
          </TimeBlockListItem>
        )),
      [berthId, firstColumn, refetch, show, timeBlocks],
    );

    return (
      <Box
        style={{
          minWidth: 150,
          maxWidth: solo ? "100%" : 150,
          margin: theme.spacing(0.5),
          marginLeft: firstColumn ? 45 : 0,
          width: solo ? "100%" : 150,
        }}
      >
        <Typography variant="body1" align="center">
          {dateFormatter({ date, format: "cccccc d.M" })}
        </Typography>
        <List>
          <Divider />
          {renderedBlocks}
          <Divider />
        </List>
      </Box>
    );
  },
  (a, b) =>
    a.date === b.date &&
    a.berthId === b.berthId &&
    a.orders === b.orders &&
    a.firstColumn === b.firstColumn,
);
